import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, Checkbox, Flex, FormControl, FormLabel,
  Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Select, SimpleGrid, Stack, Text, Textarea, useColorModeValue, useToast,
} from '@chakra-ui/react';
import {
  BMC_TIERS, BOOT_MIRROR_GB, CONTROLLERS, CPU_CACHE_MB, CPU_CORES, DISK_COUNTS, DISK_INTERFACES,
  ENDURANCE, FORM_FACTORS, LOM_PORTS, MEMORY_OPTS, MEM_SPEEDS, NIC_PORTS, NIC_SPEEDS, NODE_COUNTS,
  NodeSpec, Opt, PSU_EFFICIENCY, PSU_WATTS, RAID_LEVELS, RAILS, TPM_OPTS, ServerSpecDoc, SUPPORT,
  CUSTOM, CustomField, ceilingSpec, diskSizes, isCustom, formatGb, fromSpecJson, isHdd, specIssues, toSpecJson, totalMemoryGb,
  usableGb,
} from '../utils/serverSpecCatalog';
import { ServerSpecRow, useServerSpecActions } from '../hooks/useServerSpecs';
import apiErrorMessage from '../utils/apiError';

// item 1595 — the Tier 3 server spec EDITOR: ONE node spec × 3/5/7 identical nodes, a dropdown
// for every option (model is never chosen — the RFQ is vendor-neutral), rules checked live,
// saved with the rendered RFQ wording. A new spec opens on the ceiling (the strongest
// Fontainebleau node); tier it down to the floor (16 cores, 32 GB).

function OptSelect<T extends string | number>({
  label, value, opts, onChange,
}: { label: string; value: T; opts: Opt<T>[]; onChange: (v: T) => void }) {
  return (
    <FormControl>
      <FormLabel fontSize="xs" mb={1} opacity={0.75}>{label}</FormLabel>
      <Select
        size="sm"
        value={String(value)}
        onChange={(e) => {
          const hit = opts.find((o) => String(o.value) === e.target.value);
          if (hit) onChange(hit.value);
        }}
      >
        {opts.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
      </Select>
    </FormControl>
  );
}

// item 1595 (Chad: "add some custom type options where applicable") — a dropdown that also
// offers "Custom…": picking it reveals a text box whose text prints in place of the option.
function CustomizableSelect<K extends keyof NodeSpec>({
  label, valueKey, opts, node, field, onNode, placeholder,
}: {
  label: string; valueKey: K; opts: Opt<NodeSpec[K]>[];
  node: NodeSpec; field: CustomField; onNode: (n: NodeSpec) => void; placeholder: string;
}) {
  const value = node[valueKey];
  const custom = isCustom(node, field);
  return (
    <FormControl>
      <FormLabel fontSize="xs" mb={1} opacity={0.75}>{label}</FormLabel>
      <Select
        size="sm"
        value={custom ? CUSTOM : String(value)}
        onChange={(e) => {
          if (e.target.value === CUSTOM) {
            onNode({ ...node, custom: { ...node.custom, [field]: node.custom?.[field] ?? '' } });
            return;
          }
          const hit = opts.find((o) => String(o.value) === e.target.value);
          if (!hit) return;
          const rest = { ...node.custom };
          delete rest[field];
          onNode({ ...node, [valueKey]: hit.value, custom: rest }); // ONE update — no stale-node overwrite
        }}
      >
        {opts.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
        <option value={CUSTOM}>Custom…</option>
      </Select>
      {custom && (
        <Input
          mt={2}
          size="sm"
          autoFocus
          placeholder={placeholder}
          value={node.custom?.[field] ?? ''}
          onChange={(e) => onNode({ ...node, custom: { ...node.custom, [field]: e.target.value } })}
        />
      )}
    </FormControl>
  );
}

const Group: React.FC<{ title: string; extra?: React.ReactNode }> = ({ title, extra, children }) => (
  <Box>
    <Flex align="baseline" gap={2} mb={2}>
      <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" textTransform="uppercase" opacity={0.7}>{title}</Text>
      {extra}
    </Flex>
    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>{children}</SimpleGrid>
  </Box>
);
Group.defaultProps = { extra: undefined };

const NodeEditor: React.FC<{ node: NodeSpec; onChange: (n: NodeSpec) => void }> = ({ node, onChange }) => {
  const set = <K extends keyof NodeSpec>(k: K, v: NodeSpec[K]) => onChange({ ...node, [k]: v });
  const border = useColorModeValue('gray.200', 'whiteAlpha.200');
  const usable = usableGb(node.raid, node.diskCount, node.diskGb);
  const sizeOpts: Opt<number>[] = diskSizes(node.diskInterface).map((g) => ({ value: g, label: `${formatGb(g)} minimum` }));

  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="md" p={4}>
      <Stack spacing={5}>
        <Group title="Platform & processor">
          <OptSelect label="Form factor" value={node.formFactor} opts={FORM_FACTORS} onChange={(v) => set('formFactor', v)} />
          <OptSelect label="Physical cores (single socket)" value={node.cpuCores} opts={CPU_CORES} onChange={(v) => set('cpuCores', v)} />
          <OptSelect label="L3 cache" value={node.cpuCacheMb} opts={CPU_CACHE_MB} onChange={(v) => set('cpuCacheMb', v)} />
        </Group>

        <Group title="Memory" extra={<Badge colorScheme="purple">{`${formatGb(totalMemoryGb(node))} per node`}</Badge>}>
          <OptSelect label="Capacity (DIMMs)" value={node.memory} opts={MEMORY_OPTS} onChange={(v) => set('memory', v)} />
          <OptSelect label="Type / speed (ECC RDIMM)" value={node.memSpeed} opts={MEM_SPEEDS} onChange={(v) => set('memSpeed', v)} />
        </Group>

        <Group
          title="Storage"
          extra={usable
            ? <Badge colorScheme="green">{`${formatGb(usable)} usable`}</Badge>
            : <Badge colorScheme="red">RAID / drive count mismatch</Badge>}
        >
          <OptSelect label="Disk controller" value={node.controller} opts={CONTROLLERS} onChange={(v) => set('controller', v)} />
          <OptSelect
            label="Drive type"
            value={node.diskInterface}
            opts={DISK_INTERFACES}
            onChange={(v) => {
              // switching SSD ↔ HDD moves the size onto that family's list
              const sizes = diskSizes(v);
              onChange({ ...node, diskInterface: v, diskGb: sizes.includes(node.diskGb) ? node.diskGb : sizes[0] });
            }}
          />
          <OptSelect label="Drive count" value={node.diskCount} opts={DISK_COUNTS} onChange={(v) => set('diskCount', v)} />
          <OptSelect label="Drive size (each)" value={node.diskGb} opts={sizeOpts} onChange={(v) => set('diskGb', v)} />
          {!isHdd(node.diskInterface) && (
            <OptSelect label="Endurance" value={node.endurance} opts={ENDURANCE} onChange={(v) => set('endurance', v)} />
          )}
          <OptSelect label="RAID" value={node.raid} opts={RAID_LEVELS} onChange={(v) => set('raid', v)} />
          <OptSelect label="Boot device" value={node.bootMirrorGb} opts={BOOT_MIRROR_GB} onChange={(v) => set('bootMirrorGb', v)} />
          <FormControl display="flex" alignItems="flex-end" pb={1}>
            <Checkbox size="sm" isChecked={node.sed} onChange={(e) => set('sed', e.target.checked)}>Self-encrypting drives</Checkbox>
          </FormControl>
        </Group>

        <Group title="Network">
          <OptSelect label="Data ports" value={node.nicPorts} opts={NIC_PORTS} onChange={(v) => set('nicPorts', v)} />
          <CustomizableSelect label="Port speed" valueKey="nicSpeed" opts={NIC_SPEEDS} node={node} field="nicSpeed" onNode={onChange} placeholder="Describe the port speed / type" />
          <OptSelect label="Onboard 1 GbE" value={node.lomPorts} opts={LOM_PORTS} onChange={(v) => set('lomPorts', v)} />
          <FormControl display="flex" alignItems="flex-end" pb={1}>
            <Checkbox size="sm" isChecked={node.nicBonded} onChange={(e) => set('nicBonded', e.target.checked)}>Bonded (LACP)</Checkbox>
          </FormControl>
        </Group>

        <Group title="Management, power, security & support">
          <OptSelect label="Out-of-band management (required)" value={node.bmc} opts={BMC_TIERS} onChange={(v) => set('bmc', v)} />
          <CustomizableSelect label="Power supplies (2 × redundant)" valueKey="psuWatts" opts={PSU_WATTS} node={node} field="power" onNode={onChange} placeholder="Describe the power supplies" />
          {!isCustom(node, 'power') && (
            <OptSelect label="PSU efficiency" value={node.psuEfficiency} opts={PSU_EFFICIENCY} onChange={(v) => set('psuEfficiency', v)} />
          )}
          <CustomizableSelect label="Rails" valueKey="rails" opts={RAILS} node={node} field="rails" onNode={onChange} placeholder="Describe the rails / mounting" />
          <CustomizableSelect label="Security chip (TPM)" valueKey="tpm" opts={TPM_OPTS} node={node} field="tpm" onNode={onChange} placeholder="Describe the security chip requirement" />
          <CustomizableSelect label="Warranty (parts only)" valueKey="support" opts={SUPPORT} node={node} field="support" onNode={onChange} placeholder="e.g. 3-year parts, advance exchange" />
        </Group>
      </Stack>
    </Box>
  );
};

const ServerSpecEditorModal: React.FC<{
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  existing: ServerSpecRow | null; // null = new spec
}> = ({
  projectId, isOpen, onClose, existing,
}) => {
  const { create, update } = useServerSpecActions(projectId);
  const toast = useToast();
  const [name, setName] = useState('');
  const [nodeCount, setNodeCount] = useState<number>(3);
  const [doc, setDoc] = useState<ServerSpecDoc>(ceilingSpec);

  useEffect(() => {
    if (!isOpen) return;
    if (existing) {
      setName(existing.name);
      setNodeCount(existing.nodeCount);
      setDoc(fromSpecJson(existing.specJson));
    } else {
      setName('');
      setNodeCount(3);
      setDoc(ceilingSpec());
    }
  }, [isOpen, existing]);

  const issues = useMemo(() => specIssues(name, nodeCount, doc), [name, nodeCount, doc]);
  const saving = create.isLoading || update.isLoading;

  const onSave = async () => {
    if (issues.errors.length > 0) return;
    const body = { name: name.trim(), nodeCount, specJson: toSpecJson(nodeCount, doc) };
    try {
      if (existing) await update.mutateAsync({ id: existing.id, ...body });
      else await create.mutateAsync(body);
      onClose();
    } catch (e) {
      toast({
        title: 'Could not save the spec',
        description: apiErrorMessage(e, 'Try again.'),
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading size="md">{existing ? 'Edit server spec' : 'New Tier 3 server spec'}</Heading>
          <Text fontSize="sm" fontWeight="normal" opacity={0.7} mt={1}>
            Enterprise HA cluster of identical nodes · Ubuntu Server LTS · vendor-neutral — the manufacturer quotes anything that meets or exceeds it.
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={5}>
            <Flex gap={3} wrap="wrap" align="flex-end">
              <FormControl maxW="360px" isRequired>
                <FormLabel fontSize="xs" mb={1} opacity={0.75}>Spec name</FormLabel>
                <Input size="sm" value={name} placeholder="Cluster name" onChange={(e) => setName(e.target.value)} />
              </FormControl>
              <FormControl maxW="220px">
                <FormLabel fontSize="xs" mb={1} opacity={0.75}>Cluster nodes (HA quorum)</FormLabel>
                <Select size="sm" value={nodeCount} onChange={(e) => setNodeCount(Number(e.target.value))}>
                  {NODE_COUNTS.map((n) => <option key={n} value={n}>{`${n} identical nodes`}</option>)}
                </Select>
              </FormControl>
              <Box flex={1} />
              <Button size="sm" variant="outline" onClick={() => setDoc((d) => ({ ...ceilingSpec(), notes: d.notes, conversions: d.conversions }))}>
                Reset to ceiling
              </Button>
            </Flex>

            <NodeEditor node={doc.node} onChange={(node) => setDoc((d) => ({ ...d, node }))} />

            <FormControl>
              <FormLabel fontSize="xs" mb={1} opacity={0.75}>Notes for the manufacturer (prints on the PDF — vendors see the project number only, so leave client names out)</FormLabel>
              <Textarea size="sm" rows={3} value={doc.notes} onChange={(e) => setDoc((d) => ({ ...d, notes: e.target.value }))} />
            </FormControl>

            {issues.errors.length > 0 && (
              <Alert status="error" borderRadius="md" alignItems="flex-start">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {issues.errors.map((e) => <Text key={e}>{e}</Text>)}
                </AlertDescription>
              </Alert>
            )}
            {issues.warnings.length > 0 && (
              <Alert status="warning" borderRadius="md" alignItems="flex-start">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {issues.warnings.map((w) => <Text key={w}>{w}</Text>)}
                </AlertDescription>
              </Alert>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button colorScheme="brand" onClick={onSave} isLoading={saving} isDisabled={issues.errors.length > 0}>
            Save spec
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ServerSpecEditorModal;
